import * as d3 from "d3";
import { MovsenGlobals } from "src/components/Planner/planner/movsen-globals";
import { PlannerGlobals } from "src/components/Planner/planner/planner-globals";
import { getDColsNum, getDRowsNum, SERIAL_SPACING } from "src/components/Planner/planner/serial-layout";
import { getSerialConnectionForSerialId, getSerialConnectionProps } from "src/components/Planner/planner/serialConnections";
import {
  _does,
  arrayIntersection,
  doesDeviceFitRoom,
  line_line_intersect,
  P_numtoxy,
  P_xytonum,
  rotatePointAroundAnother,
  rotatePointAroundAnotherXY,
  segmentClosestPointToPoint,
  segmentClosestPointToPoint_S,
  snapDeviceToWall,
  uMath,
  XYto2DArr,
  XYtoNumPoly
} from "src/components/Planner/planner/uMath";
import { wallModHelper } from "src/components/Planner/planner/wallModHelper";
import { isCategoryShown } from "src/components/Planner/planner/categoryDisplayHandler";

export let UDevGlobals = {
  /**
   * The array of cached devices by function getDeviceById
   * @type {Object<string, Device[]>}
   */
  cachedDevices: {},
  serialIdMap: {},
  /**
   * @type {Wall[]}
   */
  WALLS_CACHE:  [],
  devIdSerialCache: {},
  devIsChangingTo:  {},
  devIsChangingToObj: {},
  devJustChangingInGroup: {},
};

/**
 *
 * @param {*} id The target device id
 * @returns {Device} If the device is found, the device object, else undefined
 */
export function getDeviceById(id) {
  if (UDevGlobals.cachedDevices["id_" + id]) {
    return UDevGlobals.cachedDevices["id_" + id];
  } else {
    return PlannerGlobals.devices.find(function (dev) {
      if (dev.deviceId == id) {
        UDevGlobals.cachedDevices["id_" + id] = dev;
        return true;
      } else if (dev.initialFakeDeviceId == id) {
        return true;
      }
      else {
        return false;

      }
    });
  }
}

/**
 *
 * @param {string|number} id
 * @param {"dev"|"sole"} type
 * @returns {Note}
 */
export function getNote(id, type) {
  if (type === "sole") {
    return getNoteById(id);
  }
  else if (type === "dev") {
    var dev = getDeviceById(id);
    if (dev && dev.noteTxt) {
      return {
        id: id,
        note_id: dev.noteId,
        color: dev.noteColor,
        text: dev.noteTxt,
        nopermission: dev.nopermission,
        ghost: dev.ghost || dev.noteGhost,
        /* readOnly: !PNIDHasDefaultNote(dev.product_name_id) */
      };
    }
    else {
      return false;
    }
  }
  else {
    return false;
  }
}

/**
 * @param {number} id
 * @returns {Device}
 */
export function getDeviceByIdInclChanging(id) {
  var dev = getDeviceById(id);
  if (id in UDevGlobals.devIsChangingToObj) { return UDevGlobals.devIsChangingToObj[id]; }
  else if (dev) { return dev; }
  else { return null; }
}

export function getNoteById(id) {
  return PlannerGlobals.notes.find(function (note) {
    return note.note_id === id;
  });
}

export function getSerObj(serial, dontHideLayers = false) {
  var currDevs = serial.deviceIds
  .map(function (dId) {
    return getDeviceById(dId);
  })
  .filter(function (dev) {
    return !!dev;
  })
  .map(function (dev) {
    const layerIsHidden = !isCategoryShown(dev.category_id);
    dev.layerHidden = layerIsHidden && !dontHideLayers;
    return dev;
  });


  var acc = 0;
  for (var i = 0; i < currDevs.length; i++) {
    if (!currDevs[i].layerHidden) acc++;
  }
  if (acc == 0) return { devices: [] };

  var angle = ("angle" in serial && serial.angle) ? serial.angle : 0;
  var x = currDevs[0].x, y = currDevs[0].y;

  var nameIdAlreadySeen = {};

  currDevs.forEach(function (dev) {
    dev.angle = angle;
    dev.x = x;
    dev.y = y;
    if (isDevMergedWithSerials(dev.product_name_id)) {
      var sameOnes = currDevs.filter(function (dev2) { return dev.product_name_id == dev2.product_name_id; });
      dev.qty = sameOnes.length;
      dev.similarDeviceIds = sameOnes.map(function (dev2) { return dev2.deviceId; });
      dev.sameIsGhost = !!sameOnes.find(function (dev2) { return dev2.ghost; });
    }
    else {
      dev.qty = 0;
    }
    if (!(dev.product_name_id in nameIdAlreadySeen)) {
      dev.firstOfTypeInSerial = true;
      nameIdAlreadySeen[dev.product_name_id] = true;
    } else {
      dev.firstOfTypeInSerial = false;
    }

  });

  var roomId = currDevs[0].roomId;

  currDevs = currDevs
  .filter(function (dev) {
    return !isDevMergedWithSerials(dev.product_name_id) || dev.firstOfTypeInSerial;
  })
  .filter(function (dev) {
    return !(dev.placedByChange && dev.ghost);
  });



  const toReturn = {
    id: serial.id,
    devices: currDevs,
    x: x,
    y: y,
    roomId: roomId,
    angle: angle,
    direction: serial.direction,
    height: serial.height,
    hiddenDevsNum: currDevs.filter(dev => !isCategoryShown(dev.category_id)).length
  };

  return toReturn;
}

export const uDev = {
  prodIsTreeLed(prod) {
    try {
      if (typeof prod === "object" && prod && typeof prod.text === "string") {
        const devNameSplit = prod.text.trim().split(' ').map(x => x.toLowerCase());
        return ['tree', 'led'].every(searchPart => devNameSplit.find(devNamePart => devNamePart === searchPart));
      }
      return false;
    }
    catch (err) {
      console.error(err);
      return false;
    }
  },
  /**
   *
   * @param {Device} device
   */
  getAdjustedPositionForDev(device, threshold = PlannerGlobals.DEVICE_SIZE) {
    var takenPlaces = PlannerGlobals.devices
    .filter(function (x) {
      return x.roomId === device.roomId && x.deviceId !== device.deviceId;
    })
    .map(function (x) {
      return uDev.getCenterOfDevice_NoDOM(x.deviceId);
    });
    var adjustedPos = uDev.tryNudgeDev(device, takenPlaces, threshold);
    if (adjustedPos) {
      return adjustedPos;
    }
    else {
      return null;
    }
  },
  isPositionOverlapping(position, takenCenters, threshold = PlannerGlobals.DEVICE_SIZE) {
    return takenCenters.some(function (pt) {
      return uMath.pointDistance(position, pt) < threshold;
    });
  },
  /**
   *
   * @param {Device} device
   * @param {Point[]} takenCenters
   * @param {number} threshold
   */
  tryNudgeDev(device, takenCenters, threshold = PlannerGlobals.DEVICE_SIZE) {
    var devCenter = uDev.getCenterOfDevice_NoDOM(device.deviceId);

    if (!this.isPositionOverlapping(devCenter, takenCenters, threshold)) {
      return {
        roomId: device.roomId,
        point: { x: devCenter.x - PlannerGlobals.DEVICE_SIZE / 2, y: devCenter.y - PlannerGlobals.DEVICE_SIZE / 2 },
        angle: device.angle
      };
    }

    for (var i = 1; i <= 5; i++) {
      for (var angle = 0; angle < 360; angle += 30) {
        var currentlyTriedCenter = uMath.pointAtAngleDistance(devCenter, angle, PlannerGlobals.DEVICE_SIZE * 1.05 * i);
        if (!uDev.isPositionOverlapping(currentlyTriedCenter, takenCenters, threshold)) {
          var allPts = uDev.deviceAllPts(currentlyTriedCenter, device.angle);
          var newRoom = whichRoomArePointsIn(allPts);
          if (newRoom && newRoom.id == device.roomId) {
            return {
              roomId: newRoom.id,
              point: { x: currentlyTriedCenter.x - PlannerGlobals.DEVICE_SIZE / 2, y: currentlyTriedCenter.y - PlannerGlobals.DEVICE_SIZE / 2 },
              angle: device.angle,
            };
          }
          else {
            var snapped = snapDeviceToWall(currentlyTriedCenter, PlannerGlobals.DEVICE_SIZE * 2);
            if (snapped && snapped.room.id == device.roomId) {
              if (!uDev.isPositionOverlapping(snapped.point, takenCenters, threshold)) {
                return {
                  roomId: snapped.room.id,
                  point: snapped.point,
                  angle: snapped.rotation,
                };
              }
            }
          }
        }
      }
    }
    return false;
  },
  /**
   *
   * @param {Point} pos TOP LEFT point of the device
   * @param {number} rot rotation of the device
   */
  deviceAllPts: function (pos, rot = 0) {
    var pts = [];
    var mods = [
      [0, 0],
      [0, 1],
      [1, 1],
      [1, 0]
    ];
    for (var i = 0; i < 4; i++) {
      pts.push({ x: pos.x + mods[i][0] * PlannerGlobals.DEVICE_SIZE, y: pos.y + mods[i][1] * PlannerGlobals.DEVICE_SIZE });
    }
    if ((rot % 360) != 0) {
      pts = pts.map(function (pt) {
        return rotatePointAroundAnotherXY(pt, { x: pos.x + PlannerGlobals.DEVICE_SIZE / 2, y: pos.y + PlannerGlobals.DEVICE_SIZE / 2 }, rot);
      });
    }
    return pts;
  },
  getSerialDevCenterPoints: function (serialPt, rot, len) {
    var pts = [];
    var center = {
      x: serialPt.x + getFrameWidth(len) / 2,
      y: serialPt.y + getFrameHeight() / 2
    }
    for (var i = 0; i < len; i++) {
      var dist = GET_SER_MARG() + i * (PlannerGlobals.DEVICE_SIZE + SERIAL_SPACING);
      var origidevpos = { x: serialPt.x + dist + PlannerGlobals.DEVICE_SIZE / 2, y: serialPt.y + GET_SER_MARG() + PlannerGlobals.DEVICE_SIZE / 2 };
      var rotatedpos = rotatePointAroundAnotherXY(origidevpos, center, rot);
      pts.push(rotatedpos);
    }
    return pts;
  },
  getSerialLength: function (PNIDs) {
    var pnidCount = {};
    PNIDs.forEach(function (pnid) {
      if (pnid in pnidCount) {
        pnidCount[pnid]++;
      }
      else {
        pnidCount[pnid] = 1;
      }
    });
    var len = 0;
    for (let pnid in pnidCount) {
      if (isDevMergedWithSerials(pnid)) {
        len += 1;
      }
      else {
        len += pnidCount[pnid];
      }
    }
    return len;
  },
  
  
  
  getTopLeftOfDevice_NoDOM: function (deviceId) {
    var center = uDev.getCenterOfDevice_NoDOM(deviceId);
    if (!center) {
      return center;
    }
    return { x: center.x - PlannerGlobals.DEVICE_SIZE / 2, y: center.y - PlannerGlobals.DEVICE_SIZE / 2 };
  },
  
  getCenterOfDevice_NoDOM: function (deviceId) {
      var dev = getDeviceById(deviceId);
      if (!dev) {
        return false;
      }
      var ser = getSerialOfDeviceId(deviceId);
      if (ser) {
        var devIdx = idxOfDevInSerial(deviceId, ser);
        if (devIdx == -1) {
          return false;
        }
        const conn = getSerialConnectionForSerialId(ser.id);
        const connProps = conn ? getSerialConnectionProps(conn.id) : null;
        const serInConnProps = connProps ? connProps.serials.find(connPropSer => connPropSer.serialId == ser.id) : null;
        const isVertical = ser.direction === "vertical"; /* isSerialVertical(ser.id); */

        var firstDev, serRot;
        var ghostProps = ghostDevProps();

        if (ghostProps && ghostProps.entityType == "serial" && ghostProps.entityId == ser.id) {
          firstDev = { x: ghostProps.x, y: ghostProps.y };
          serRot = ghostProps.rot;
        }
        else {
          firstDev = getDeviceById(ser.deviceIds[0]);
          serRot = ser.angle || 0;
        }

        var firstCenter = { x: firstDev.x + PlannerGlobals.DEVICE_SIZE / 2, y: firstDev.y + PlannerGlobals.DEVICE_SIZE / 2 };

        if (serInConnProps) {
          firstCenter.y += serInConnProps.rowIdx * (PlannerGlobals.DEVICE_SIZE + SERIAL_SPACING);

          const xDiff = (connProps.allColsNum - serInConnProps.colNum) / 2
          firstCenter.x += xDiff * (PlannerGlobals.DEVICE_SIZE + SERIAL_SPACING);
        }

        var devPt = { x: firstCenter.x, y: firstCenter.y };
        if (isVertical) {
          devPt.y += devIdx * (PlannerGlobals.DEVICE_SIZE + SERIAL_SPACING);
        }
        else {
          devPt.x += devIdx * (PlannerGlobals.DEVICE_SIZE + SERIAL_SPACING);
        }

        var serLen = serialRealLength(ser);
        var serCenterDiff = serLen * PlannerGlobals.DEVICE_SIZE + (serLen - 1) * SERIAL_SPACING;
        var serCenter = null;

        if (serInConnProps) {
          const ser2d = getSerialCenter2d(connProps.allColsNum, connProps.allRowsNum).split(' ').map(x => +x);
          serCenter = {
            x: firstDev.x + ser2d[0],
            y: firstDev.y + ser2d[1]
          }
        }
        else if (isVertical) {
          serCenter = { x: firstDev.x + PlannerGlobals.DEVICE_SIZE / 2, y: firstDev.y + serCenterDiff / 2 };
        }
        else {
          serCenter = { x: firstDev.x + serCenterDiff / 2, y: firstDev.y + PlannerGlobals.DEVICE_SIZE / 2 };
        }

        var rotated = rotatePointAroundAnotherXY(devPt, serCenter, serRot);
        return rotated;
      }
      else {
        var ghostProps = ghostDevProps();
        if (ghostProps && ghostProps.entityType == "device" && ghostProps.entityId == deviceId) {
          return { x: ghostProps.cx, y: ghostProps.cy };
        }
        return { x: dev.x + PlannerGlobals.DEVICE_SIZE / 2, y: dev.y + PlannerGlobals.DEVICE_SIZE / 2 };
      }
    },
  
  getCornersOfDevice_NoDOM: function (deviceId) {
      var center = this.getCenterOfDevice_NoDOM(deviceId);
      if (!center) {
        return false;
      }
      var corners = [];
      for (var i = -1; i <= 1; i += 2) {
        for (var j = -1; j <= 1; j += 2) {
          corners.push({ x: center.x + i * PlannerGlobals.DEVICE_SIZE / 2, y: center.y + j * PlannerGlobals.DEVICE_SIZE / 2 });
        }
      }
      return corners;
    },
  
  getAngleOfDevice: function (deviceId) {
    var dev = getDeviceById(deviceId);
    if (!dev) {
      return false;
    }
    var ser = getSerialOfDeviceId(deviceId);
    if (ser) {
      var devIdx = idxOfDevInSerial(deviceId, ser);
      if (devIdx == -1) {
        return false;
      }
      var serRot;
      var ghostProps = ghostDevProps();
      if (ghostProps && ghostProps.entityType == "serial" && ghostProps.entityId == ser.id) {
        serRot = ghostProps.rot;
      }
      else {
        serRot = ser.angle || 0;
      }

      return serRot;
    }
    else {
      var ghostProps = ghostDevProps();
      if (ghostProps && ghostProps.entityType == "device" && ghostProps.entityId == deviceId) {
        return ghostProps.rot;
      }
      return dev.angle || 0;
    }
  },
  
  /**
   *
   * @param {Room} room
   */
  isRoomConvex: function (room) {
    return uMath.isPolygonConvex(room.poly);
  },
  isRoomFacingOutOuter: function (room, outerWallIds) {
    return arrayIntersection(outerWallIds, room.poly.map(function (pt) { return pt.id })).length > 0;
  },
  /**
   *
   * @param {number} roomId
   * @param {"window"|"door"} wallModType
   * @param {function} filterCallback
   * @param {number} maxAllowedDist
   */
  groupDevsWithWallMods(roomId, wallModType, filterCallback, maxAllowedDist = PlannerGlobals.DEVICE_SIZE * 3) {
    var res = {};
    var devs = PlannerGlobals.devices.filter(filterCallback);
    var wallMods = wallModHelper.getWallModsOfRoomByType(roomId, wallModType);

    for (var i in wallMods) {
      var wallMod = wallMods[i];
      res[wallMod.id] = devs.filter(function (dev) {
        return uMath.pointSegmentDistance(dev, wallMod.points[0], wallMod.points[1]) < maxAllowedDist;
      });
      devs = devs.filter(function (dev) {
        return res[wallMod.id].findIndex(function (fi) {
          return fi == dev;
        }) == -1;
      });
      if (res[wallMod.id].length == 0) {
        delete res[wallMod.id];
      }
    }

    return res;
  },
  getSerialRectLineIntersect: function (serialId, line) {
    var serialObj = getSerialById(serialId);

    if (serialObj) {
      /* var serialD = getSerialD(serialId);
      var serialCenter = getSerialCenterPos(serialD);
      var width = getFrameWidth(serialD.devices.length);
      var height = getFrameHeight();
      var serialCoords = [serialCenter[0] - width / 2, serialCenter[1] - height / 2];

      var serialRectBox = {
          x: serialCoords[0],
          y: serialCoords[1],
          width: width,
          height: height,
      }; */

      const posProps = getSerialPosProps(serialObj.deviceIds.length, serialObj.direction);
      const serialCenter = P_xytonum(getSerialCenterPos2(serialObj));
      var serialRectBox = {
        x: serialCenter[0] - posProps.width / 2,
        y: serialCenter[1] - posProps.height / 2,
        width: posProps.width,
        height: posProps.height,
      };
      var SP = {
        'topleft': [serialRectBox.x, serialRectBox.y],
        'topright': [serialRectBox.x + serialRectBox.width, serialRectBox.y],
        'bottomleft': [serialRectBox.x, serialRectBox.y + serialRectBox.height],
        'bottomright': [serialRectBox.x + serialRectBox.width, serialRectBox.y + serialRectBox.height],
      };

      var angle = getSerialAngle2(serialObj);

      for (var pt in SP) {
        SP[pt] = rotatePointAroundAnother(SP[pt], serialCenter, angle);
      }

      var serialRectLines = [
        { x1: SP.topleft[0], y1: SP.topleft[1], x2: SP.topright[0], y2: SP.topright[1] }, // upper
        { x1: SP.bottomleft[0], y1: SP.bottomleft[1], x2: SP.bottomright[0], y2: SP.bottomright[1] }, // lower
        { x1: SP.topleft[0], y1: SP.topleft[1], x2: SP.bottomleft[0], y2: SP.bottomleft[1] }, // left
        { x1: SP.topright[0], y1: SP.topright[1], x2: SP.bottomright[0], y2: SP.bottomright[1] }, // right
      ];

      for (var serialLine of serialRectLines) {
        var is = line_line_intersect(serialLine, line);
        if (is != 'nir') {
          return is;
        }
      }
    }
  },
  numberOfProductInRoom: function (room, pnid) {
    if (!room) {
      return 0;
    }
    return PlannerGlobals.devices.filter(function (dev) {
      return dev.product_name_id == pnid && dev.roomId == room.id;
    }).length;
  },
  /**
   *
   * @param {Room} room
   * @param {number[]} pnidArr
   */
  numberOfProductsInRoom: function (room, pnidArr) {
    if (!room) {
      return 0;
    }
    return PlannerGlobals.devices.filter(function (dev) {
      return dev.roomId == room.id && _does(pnidArr).contain(+dev.product_name_id);
    }).length;
  },
  /**
   *
   * @param {number} roomId
   * @returns {Device[]}
   */
  devicesOfRoom: function (roomId, pnid = null) {
    if (!roomId) {
      return [];
    }
    return PlannerGlobals.devices.filter(function (dev) {
      return dev.roomId == roomId && (!pnid || dev.product_name_id == pnid);
    });
  },
  devicesOfRoomPnidArr: function (roomId, pnidArr) {
    return PlannerGlobals.devices.filter(function (dev) {
      return dev.roomId == roomId && _does(pnidArr).contain(dev.product_name_id);
    });
  },
  /**
   *
   * @param {number} connId
   * @returns {Device[]}
   */
  elemsNearWallMod: function (connId, pnid, sensitivity = PlannerGlobals.DEVICE_SIZE * 2) {
    var elems = [];
    var mods = wallModHelper.getWallModsByConnId(connId);
    mods.forEach(function (mod) {
      var devs = uDev.devicesOfRoom(mod.roomId, pnid);
      mod.points.forEach(function (point) {
        devs.forEach(function (dev) {
          var center = uDev.getCenterOfDevice_NoDOM(dev.deviceId);
          if (uMath.pointDistance(center, point) < sensitivity) {
            elems.push(dev);
          }
        });
      });
    });
    return elems;
  },
  /**
   *
   * @param {WallMod} wallMod
   * @param {number} pnid
   * @param {number} sensitivity
   * @returns {Device[]}
   */
  elemsNearWallMod2: function (wallMod, pnid, sensitivity = PlannerGlobals.DEVICE_SIZE * 1.5) {
    var elems = [];
    var devs = uDev.devicesOfRoom(wallMod.roomId, pnid);

    devs.forEach(function (dev) {
      var center = uDev.getCenterOfDevice_NoDOM(dev.deviceId);
      if (uMath.pointSegmentDistance(center, wallMod.points[0], wallMod.points[1]) < sensitivity) {
        elems.push(dev);
      }
    });

    return elems;
  },
  /**
   *
   * @param {WallMod} wallMod
   * @param {number[]} pnid
   * @param {number} sensitivity
   * @returns {Device[]}
   */
  elemsNearWallMod2PnidArr: function (wallMod, pnids, sensitivity = PlannerGlobals.DEVICE_SIZE * 1.5) {
    var elems = [];
    var devs = uDev.devicesOfRoomPnidArr(wallMod.roomId, pnids);

    devs.forEach(function (dev) {
      var center = uDev.getCenterOfDevice_NoDOM(dev.deviceId);
      if (uMath.pointSegmentDistance(center, wallMod.points[0], wallMod.points[1]) < sensitivity) {
        elems.push(dev);
      }
    });

    return elems;
  },
  /**
   *
   * @param {WallMod} wallMod
   * @param {number} sensitivity
   */
  serialsNearWallMod: function (wallMod, sensitivity = PlannerGlobals.DEVICE_SIZE * 1.5) {
    return uDev.devicesOfRoom(wallMod.roomId)
    .filter(function (dev) {
      if (!dev.serializable) {
        return false;
      }

      var center = uDev.getCenterOfDevice_NoDOM(dev.deviceId);
      return uMath.pointSegmentDistance(center, wallMod.points[0], wallMod.points[1]) < sensitivity
    })
    .map(function (dev) {
      return getSerialOfDeviceId(dev.deviceId);
    })
    .filter(function (ser) {
      return !!ser;
    });
  },
  isPlaceTaken: function (pt, devs, threshold = PlannerGlobals.DEVICE_SIZE / 2) {
    for (var i = 0; i < devs.length; i++) {
      var dev = devs[i];
      var pos = uDev.getCenterOfDevice_NoDOM(dev.deviceId);
      var dist = uMath.pointDistance(pt, pos);
      if (dist < threshold) {
        return /* true */ dev;
      }
    }
    return false;
  },
  /**
   *
   * @param {Point} point
   * @returns {{x, y, dist, roomId, wallId}}
   */
  closestWallPtToPoint: function (point, threshold = Infinity) {
    if (!UDevGlobals.WALLS_CACHE || !UDevGlobals.WALLS_CACHE.length) {
      return false;
    }
    var pts = [];
    UDevGlobals.WALLS_CACHE.forEach(function (wall) {
      var closestPt = segmentClosestPointToPoint(point, { x: wall.x1, y: wall.y1 }, { x: wall.x2, y: wall.y2 });
      var dist = uMath.pointDistance(point, closestPt);
      if (dist < threshold) {
        pts.push({ x: closestPt.x, y: closestPt.y, dist: dist, roomId: wall.roomId, wallId: wall.id });
      }
    });

    pts.sort(function (a, b) {
      return a.dist < b.dist ? -1 : 1;
    });

    pts = pts.filter(function (pt) {
      return uMath.pointDistance(pt, pts[0]) < 0.01;
    });

    return pts;
  },
  /**
   *
   * @param {Point} point
   * @param {WallMod[]} wallMods
   */
  closestWallModToPoint: function (point, wallMods) {
    if (wallMods.length == 0) {
      return false;
    }
    var dists = wallMods.map(function (wallMod) {
      return { wallMod, dist: uMath.pointSegmentDistance(point, wallMod.points[0], wallMod.points[1]) };
    });
    dists.sort(function (a, b) {
      return a.dist < b.dist ? -1 : 1;
    });
    return dists[0].wallMod;
  },

  UNASSIGNED_ROOM_ID: -501,
  UNASSIGNED_ROOM_NAME: "Unassigned",

  OUTDOORS_ROOM_PROTOTYPE: null,
  OUTDOORS_ROOM_POLY: [{ x: 0, y: 0, id: 'OP_1' }, { x: 0, y: 900, id: 'OP_2' }, { x: 900, y: 900, id: 'OP_3' }, { x: 900, y: 0, id: 'OP_4' }],

  OUTDOORS_ROOM_ID: -500,
  OUTDOORS_ROOM_NAME: "",
  get VIRTUAL_OUTDOORS_ROOM() {
    var outdoorsRoom = {
      poly: uDev.OUTDOORS_ROOM_POLY,
      id: uDev.OUTDOORS_ROOM_ID,
      roomname: uDev.OUTDOORS_ROOM_NAME,
    };
    for (var key in uDev.OUTDOORS_ROOM_PROTOTYPE) {
      if (!(key in outdoorsRoom)) {
        outdoorsRoom[key] = uDev.OUTDOORS_ROOM_PROTOTYPE[key];
      }
    }
    return outdoorsRoom;
  },

  get VIRTUAL_UNASSIGNED_ROOM() {
    var unassignedRoom = {
      poly: [
        { x: 0, y: 0, id: 'UP_1', },
        { x: 0, y: 900, id: 'UP_2', },
        { x: 900, y: 900, id: 'UP_3', },
        { x: 900, y: 0, id: 'UP_4', },
      ],
      id: uDev.UNASSIGNED_ROOM_ID,
      roomname: uDev.UNASSIGNED_ROOM_NAME
    };
    return unassignedRoom;
  },

  /**
   * returns the rooms of the planner, plus the "virtual" outdoors room.
   * other virtual rooms may be added in the future
   * @returns {Room[]}
   */
  get roomsExtended() {
    return PlannerGlobals.rooms.concat([this.VIRTUAL_OUTDOORS_ROOM]);
  },

  get roomsExtendedWithUnassigned() {
    return PlannerGlobals.rooms.concat([this.VIRTUAL_OUTDOORS_ROOM, this.VIRTUAL_UNASSIGNED_ROOM]);
  },

  

  

  

  

  

  

  

  

  

  

  /**
   * for debugging purposes, we get all the devices that are completely outside their rooms
   * because right now outdoor devices are placed in the first room of the plan
   */
  debug_setOuterDevs: function () {
    /* devices.forEach(function (dev) {
        var corners = uDev.getCornersOfDevice_NoDOM(dev.deviceId);
        var roomPoly = XYto2DArr(getRoomById(dev.roomId).poly);
        if (corners.every(function (corner) {
            return !d3.polygonContains(roomPoly, P_xytonum(corner));
        })) {
            dev.roomId = uDev.OUTDOORS_ROOM_ID;
        }
    }); */
  },

  /**
   * returns true if the outer border (orange line) exists
   */
  outerBorderExists: function () {
    return typeof PlannerGlobals.outerBorder != "undefined" && Array.isArray(PlannerGlobals.outerBorder) && PlannerGlobals.outerBorder.length > 0;
  },

  

  

  getOutdoorRoomPolies: function () {
    return PlannerGlobals.rooms.filter(function (room) {
      return isRoomOutside(room, PlannerGlobals.outerBorder);
    }).map(function (room) {
      return XYto2DArr(room.poly);
    });
  },

  


  
  /**
   *
   * @param {Point} topLeftPt
   * @param {number} rotation
   */
  anyOutdoorRoomIntersectsDev: function (topLeftPt, rotation) {
    if (this.outerBorderExists()) {
      var allPts = this.deviceAllPts(topLeftPt, rotation);
      var outdoorRoomWalls = PlannerGlobals.rooms
      .filter(function (room) { return isRoomOutside(room, PlannerGlobals.outerBorder); })
      .map(function (room) {
        return wallsOfRoom(room);
      })
      .reduce(function (p, c) { return p.concat(c); }, []);
      for (var wall of outdoorRoomWalls) {
        if (uMath.segmentPolygonIntersection(wall, allPts)) {
          return true;
        }
      }
    }
    return false;
  },



}

/**
 *
 * @param {Device} dev
 * @returns {boolean}
 */
export function devIsCabinet(dev) {
  return 'type' in dev && dev.type.toString().indexOf("cabinet") != -1;
}

/**
 *
 * @param {Device} dev
 * @returns {boolean}
 */
export function devIsCustom(dev) {
  return typeof dev.deviceId === "string" && dev.deviceId.indexOf('C_') === 0;
}

export function GET_SER_MARG() {
  return SER_MARG * PlannerGlobals.DEVICE_SIZE / 20;
}

export function GET_SER_BORDER_WIDTH() {
  return SER_BORDER_WIDTH * PlannerGlobals.DEVICE_SIZE / 20;
}

export const SER_MARG = 2;
export const SER_BORDER_WIDTH = 0.5;

export function getFrameWidth(devNum) {
  if (!devNum) { return 0; }
  return GET_SER_MARG() * 2 + devNum * (PlannerGlobals.DEVICE_SIZE) + (devNum - 1) * SERIAL_SPACING;
}

export function getFrameHeight() {
  return GET_SER_MARG() + PlannerGlobals.DEVICE_SIZE + GET_SER_MARG();
}

/**
 * @deprecated will be deprecated in favor of getSerialPosProps
 * @param {*} devNum
 * @returns
 */
export function getSerialCenter(devNum) {
  return ((getFrameWidth(devNum) - GET_SER_MARG() * 2) / 2).toString() + " " + ((getFrameHeight() - GET_SER_MARG() * 2) / 2).toString();
}

export function getSerialCenter2d(colsNum, rowsNum) {
  const x = ((getFrameWidth(colsNum) - GET_SER_MARG() * 2) / 2).toString();
  const y = ((getFrameWidth(rowsNum) - GET_SER_MARG() * 2) / 2).toString();
  return x + " " + y;
}

/**
 * @deprecated will be deprecated in favor of getSerialPosProps
 * @param {*} devNum
 * @returns
 */
export function getSerialCenterXY(devNum) {
  return P_numtoxy(getSerialCenter(devNum).split(" ").map(function (coord) { return +coord; }));
}

export function getSerialPosProps(devNum, direction = "horizontal") {
  if (!devNum) { return { width: 0, height: 0, centerOffsetX: 0, centerOffsetY: 0 }; }
  const width = getFrameWidth(devNum);
  const height = getFrameHeight();
  const centerOffsetX = (getFrameWidth(devNum) - GET_SER_MARG() * 2) / 2;
  const centerOffsetY = (getFrameHeight() - GET_SER_MARG() * 2) / 2;
  if (direction === "horizontal") {
    return {
      width,
      height,
      centerOffsetX,
      centerOffsetY
    }
  }
  else {
    return {
      width: height,
      height: width,
      centerOffsetX: centerOffsetY,
      centerOffsetY: centerOffsetX
    }
  }
}

export function devicesOfSerial(serialId) {
  var ser = getSerialById(serialId);
  if (!ser) {
    return false;
  }
  return ser.deviceIds.map(getDeviceById);
}

export function devicesOfSerialConnection(d) {
  if (!d.connId) {
    return false;
  }
  return d.serials
  .map(ser => devicesOfSerial(ser.id))
  .filter(x => !!x)
  .reduce((p, c) => p.concat(c), []);
}

/**
 *
 * @param {number} groupId
 * @returns {Device[] | false}
 */
export function devicesOfGroup(groupId) {
  var group = getGroupById(groupId);
  if (!group) {
    return false;
  }
  return group.deviceIds.map(getDeviceById);
}

/**
 *
 * @param {number} groupId
 * @returns {Device[] | false}
 */
export function inputsOfGroup(groupId) {
  var devs = devicesOfGroup(groupId);
  if (!devs) { return false; }
  return devs.filter(dev => dev.input);
}

/**
 *
 * @param {number} groupId
 * @returns {Device[] | false}
 */
export function outputsOfGroup(groupId) {
  var devs = devicesOfGroup(groupId);
  if (!devs) { return false; }
  return devs.filter(dev => !dev.input);
}

export function serialRealLength(serial) {
  var devs = serial.deviceIds.map(getDeviceById);
  var places = [];
  devs.forEach(function (dev) {
    if (!isDevMergedWithSerials(dev.product_name_id)) {
      places.push({ pnid: dev.product_name_id, devs: [dev] });
    }
    else {
      var foundPlace = places.find(function (place) {
        return place.pnid == dev.product_name_id;
      });
      if (foundPlace) {
        foundPlace.devs.push(dev);
      }
      else {
        places.push({ pnid: dev.product_name_id, devs: [dev] });
      }
    }
  });
  return places.length;
}

export function idxOfDevInSerial(deviceId, serial) {
  var deviceId = +deviceId;
  if (serial.deviceIds.indexOf(deviceId) == -1) {
    return -1;
  }
  var devs = serial.deviceIds.map(getDeviceById);
  var places = [];
  devs.forEach(function (dev) {
    if (!isDevMergedWithSerials(dev.product_name_id)) {
      places.push({ pnid: dev.product_name_id, devs: [dev] });
    }
    else {
      var foundPlace = places.find(function (place) {
        return place.pnid == dev.product_name_id;
      });
      if (foundPlace) {
        foundPlace.devs.push(dev);
      }
      else {
        places.push({ pnid: dev.product_name_id, devs: [dev] });
      }
    }
  });
  var index = places.findIndex(function (place) {
    return place.devs.findIndex(function (dev) {
      return dev.deviceId == deviceId;
    }) != -1;
  });
  return index;
}

let mergedDevs = [
  1, // basic (SO)
  2, // water protected (SO)
  3, // child protected (SO)
];

/**
 * This is for checking if a device is BADGED.
 * @param {number} pnid
 *
 */
export function isDevMergedWithSerials(pnid) {
  return mergedDevs.findIndex(function (currId) {
    return currId == pnid;
  }) != -1;
}

export function isDeviceMerged(dev) {
  return isDeviceMergedById(dev.deviceId);
}

export function isDeviceMergedById(devId){

  devId = parseInt(devId);

  let mergeDeviceArray = PlannerGlobals.mergedDevice.filter(merged => merged.merge_plan_objects_id === devId);

  if (mergeDeviceArray.length > 0) {
    return true;
  }

  let mergedDeviceArray = PlannerGlobals.mergedDevice.filter(merged => merged.merged_plan_objects_id === devId);

  if (mergedDeviceArray.length > 0) {
    return true;
  }

  return false;
}

/**
 *
 * @param {Room} room
 * @returns {Wall[]}
 */
export function wallsOfRoom(room) {
  if (!room || !room.poly) {
    return [];
  }
  var walls = [];

  for (var currPt = 0; currPt < room.poly.length; currPt++) {
    var c = room.poly[currPt];
    var n = room.poly[(currPt + 1) % room.poly.length];
    walls.push({
      x1: Math.round(c.x * 10000) / 10000,
      y1: Math.round(c.y * 10000) / 10000,
      x2: Math.round(n.x * 10000) / 10000,
      y2: Math.round(n.y * 10000) / 10000,
      roomId: room.id,
      id: c.id,
      order_index: c.order_index
    });
  }

  return walls;
}

/**
 *
 * @param {Room} room
 * @param {number[]} outerWallIds
 */
export function outerWallsOfRoom(room, outerWallIds) {
  var allWalls = wallsOfRoom(room);
  return allWalls.filter(function (wall) {
    return outerWallIds.indexOf(wall.id) != -1;
  });
}

/**
 *
 * @param {Room} onlyInRoom
 */
export function rebuildWallsCache(onlyInRoom = null, includeOuterBorder = false, includeOutdoor = false) {
  UDevGlobals.WALLS_CACHE = [];
  if (onlyInRoom) {
    UDevGlobals.WALLS_CACHE = wallsOfRoom(onlyInRoom);
  } else {
    for (var i = 0; i < PlannerGlobals.rooms.length; i++) {
      UDevGlobals.WALLS_CACHE = UDevGlobals.WALLS_CACHE.concat(wallsOfRoom(PlannerGlobals.rooms[i]));
    }
  }

  if (includeOutdoor) {
    var outdoorRoom = getRoomById(uDev.OUTDOORS_ROOM_ID);
    if (outdoorRoom) {
      UDevGlobals.WALLS_CACHE = UDevGlobals.WALLS_CACHE.concat(wallsOfRoom(outdoorRoom));
    }
  }

  if (includeOuterBorder) {
    var outerBorderWalls = getOuterBorderWallsCache();
    if (outerBorderWalls) {
      UDevGlobals.WALLS_CACHE = UDevGlobals.WALLS_CACHE.concat(outerBorderWalls);
    }
  }
  return UDevGlobals.WALLS_CACHE;
}

export function getOuterBorderWallsCache() {
  if (uDev.outerBorderExists()) {
    var haveToInvert = d3.polygonArea(XYto2DArr(PlannerGlobals.outerBorder)) >= 0;
    var outerBorderLines = uMath.segmentsFromPointArray(PlannerGlobals.outerBorder);
    outerBorderLines.forEach(function (seg) {
      seg.roomId = uDev.OUTDOORS_ROOM_ID;
      seg.haveToInvertRot = haveToInvert;
      seg.outerBorder = true;
    });
    return outerBorderLines;
  }
  else {
    return null;
  }
}

/**
 *
 *
 * @param {number} id
 * @returns {Room}
 */
export function getRoomById(id) {
  id = +id;

  if (typeof PlannerGlobals.allRooms != 'undefined' && Array.isArray(PlannerGlobals.allRooms)) {
    return PlannerGlobals.allRooms.find(function (room) {
      return room.id === id;
    });
  }

  return uDev.roomsExtendedWithUnassigned.find(function (room) {
    return room.id === id;
  });
}

export function getModRoomIfId(id) {
  id = +id;
  if (typeof PlannerGlobals.modifiedRoom === "object" && PlannerGlobals.modifiedRoom.id === id) {
    return PlannerGlobals.modifiedRoom;
  }
  return false;
}

/**
 *
 * @param {Room[]} rooms
 * @param {Point[]} outerBorder
 */
export function getInnerRooms(rooms, outerBorder) {
  if (!rooms || !Array.isArray(rooms) || rooms.length == 0) {
    return [];
  }
  else if (!uDev.outerBorderExists()) {
    return rooms;
  }
  var outerWallPoly = XYtoNumPoly(outerBorder);
  return rooms.filter(function (room) {
    return room.poly.some(function (pt) {
      return d3.polygonContains(outerWallPoly, P_xytonum(pt));
    });
  });
}

/**
 *
 * @param {Room[]} rooms
 * @param {Point[]} outerBorder
 */
export function getOuterRooms(rooms, outerBorder) {
  if (!rooms || !Array.isArray(rooms) || rooms.length == 0) {
    return [];
  }
  else if (!uDev.outerBorderExists()) {
    return [];
  }
  var outerWallPoly = XYtoNumPoly(outerBorder);
  return rooms.filter(function (room) {
    return !room.poly.some(function (pt) {
      return d3.polygonContains(outerWallPoly, P_xytonum(pt));
    });
  });
}

export function isRoomOutside(room, outerBorder) {
  var outerWallPoly = XYtoNumPoly(outerBorder);
  return !room.poly.some(function (pt) {
    return d3.polygonContains(outerWallPoly, P_xytonum(pt));
  });
}

/**
 *
 * @param {Point} point
 * @param {Room[]} roomsArr
 */
export function whichRoomIsPointInFromRooms(point, roomsArr) {
  for (var i = 0; i < roomsArr.length; i++) {
    if (d3.polygonContains(XYtoNumPoly(roomsArr[i].poly), P_xytonum(point))) {
      return roomsArr[i];
    }
  }
  return false;
}

/**
 *
 * @param {Point} point
 */
export function whichRoomIsPointIn(point) {
  for (var i = 0; i < PlannerGlobals.rooms.length; i++) {
    if (d3.polygonContains(XYtoNumPoly(PlannerGlobals.rooms[i].poly), P_xytonum(point))) {
      return PlannerGlobals.rooms[i];
    }
  }
  return false;
}

/**
 *
 * @param {Point[]} points
 */
export function whichRoomArePointsIn(points) {
  for (var i = 0; i < PlannerGlobals.rooms.length; i++) {
    if (points.every(function (point) {
      return d3.polygonContains(XYtoNumPoly(PlannerGlobals.rooms[i].poly), P_xytonum(point))
    })) {
      return PlannerGlobals.rooms[i];
    }
  }
  return false;
}

/**
 *
 * @param {Point} point
 */
export function whichRoomIsPointInInclOutdoors(point) {
  var xyPoint = P_xytonum(point);
  for (var i = 0; i < PlannerGlobals.rooms.length; i++) {
    if (d3.polygonContains(XYtoNumPoly(PlannerGlobals.rooms[i].poly), xyPoint)) {
      return PlannerGlobals.rooms[i];
    }
  }
  // not in a room, check outdoors
  if (uDev.outerBorderExists()) {
    if (d3.polygonContains(XYtoNumPoly(uDev.OUTDOORS_ROOM_POLY), xyPoint) &&
        !d3.polygonContains(XYtoNumPoly(PlannerGlobals.outerBorder), xyPoint))
      return uDev.VIRTUAL_OUTDOORS_ROOM;
  }
  return false;
}

/**
 *
 * @param {Point} point
 * @returns {boolean}
 */
export function whichRoomIsPointInInclOutdoorsAndUnassigned(point) {
  const whichRoom = whichRoomIsPointInInclOutdoors(point);
  if (whichRoom) { return whichRoom; }

  // at this point, its not in any drawn room or outdoors. checking if it is in 0,0 -> 900,900 rect

  const isInBounds = d3.polygonContains([[0, 0], [0, 900], [900, 900], [900, 0]], P_xytonum(point));

  return isInBounds ? uDev.VIRTUAL_UNASSIGNED_ROOM : false;
}
/**
 *
 * @param {Point} point
 */
export function closestRoomToPoint(point, rooms) {
  if (!rooms.length) {
    return false;
  }
  var inRoom = whichRoomIsPointIn(point);
  if (inRoom) {
    return { room: inRoom, outside: false };
  }
  var distances = rooms.map(function (room) {
    var closestPt = uMath.pointClosestPtToPolygon(point, room.poly);
    return { room, distance: uMath.pointDistance(closestPt, point), closestPt, outside: true };
  });
  distances.sort(function (a, b) {
    return a.distance < b.distance ? -1 : 1;
  });
  return distances[0];
}

/**
 * @returns {Serial}
 */
export function getSerialById(id) {
  if (UDevGlobals.serialIdMap[id]) { return UDevGlobals.serialIdMap[id]; }
  id = +id;
  var ser = PlannerGlobals.serials.find(function (serial) {
    return serial.id == id || serial.initialFakeSerialId == id;
  });
  if (ser) {
    const firstDev = getDeviceById(ser.deviceIds[0]);
    if (firstDev) {
      ser.roomId = getDeviceById(ser.deviceIds[0]).roomId;
    }
    UDevGlobals.serialIdMap[id] = ser;
    return ser;
  }
  return undefined;
}

export function getSerialRoom(serialId) {
  return getDeviceById(getSerialById(serialId).deviceIds[0]).roomId;
}

/**
 *
 * @param {number} deviceId
 * @returns {Serial}
 */
export function getSerialOfDeviceId(deviceId) {
  /*  deviceId = deviceId.toString().replace('S_', '');
   deviceId = +deviceId; */
  if (UDevGlobals.devIdSerialCache[deviceId]) {
    return UDevGlobals.devIdSerialCache[deviceId];
  }
  var serial = PlannerGlobals.serials.find(function (serial) {
    return serial.deviceIds.findIndex(function (currDeviceId) {
      return deviceId == currDeviceId;
    }) != -1;
  });

  if (serial) {
    UDevGlobals.devIdSerialCache[deviceId] = serial;
  }
  return serial;
}

/**
 *
 * @param {number} deviceId
 * @returns {Group}
 */
export function getGroupOfDevice(deviceId) {
  return PlannerGlobals.groups.find(function (group) {
    return group.deviceIds.findIndex(function (deviceIdCurr) {
      return deviceIdCurr == deviceId;
    }) != -1;
  });
}

/**
 *
 * @param {number} deviceId
 * @returns {Group[]}
 */
export function getGroupsOfDevice(deviceId) {
  return PlannerGlobals.groups.filter(function (group) {
    return group.deviceIds.findIndex(function (deviceIdCurr) {
      return deviceIdCurr == deviceId;
    }) != -1;
  });
}

/**
 *
 * @param {number} groupId
 * @returns {Group}
 */
export function getGroupById(groupId) {
  return PlannerGlobals.groups.find(function (group) {
    return group.id === groupId;
  });
}

/**
 *
 * @param {Group} grp
 * @returns {boolean}
 */
export function groupHasGhostDev(grp) {
  return grp.deviceIds.map(getDeviceByIdInclChanging).filter(function (dev) { return dev.ghost; }).length > 0;
}

export function thereWasGhostDevice() {
  return !!PlannerGlobals.GHOST_DEV_PARAMS;
}

export function ghostDevProps() {
  return PlannerGlobals.GHOST_DEV_PARAMS;
}

export function isDevChangeInProgress() {
  return Object.keys(UDevGlobals.devIsChangingTo).length > 0;
}

export function isDInGroup(d) {
  return d.devices.findIndex(function (dev) {
    return !!getGroupOfDevice(dev.deviceId);
  }) != -1;
}

/**
 *
 * @param {D3Device} d
 * @returns {Group[]}
 */
export function getGroupsOfD(d) {
  return getDistinctGroupsOfDevs(d.devices);
}

/**
 *
 * @param {Device[]} devices
 * @returns {Group[]}
 */
export function getDistinctGroupsOfDevs(devices) {
  var alreadySeenGroups = {};
  var groups = [];

  devices.forEach(function (dev) {
    var grps = getGroupsOfDevice(dev.deviceId);
    grps.forEach(function (grp) {
      if (!alreadySeenGroups[grp.id]) {
        alreadySeenGroups[grp.id] = true;
        groups.push(grp);
      }
    });
  });

  return groups;
}

/**
 *
 * @param {Device[]} devices
 * @returns {SerialConnection[]}
 */
export function getDistinctSerialConnectionsOfDevs(devices) {
  const alreadySeenConns = {};
  const serialConnections = [];

  devices.forEach(function (dev) {
    const ser = getSerialOfDeviceId(dev.deviceId);
    if (ser) {
      const conn = getSerialConnectionForSerialId(ser.id);
      if (conn && !alreadySeenConns[conn.id]) {
        alreadySeenConns[conn.id] = true;
        serialConnections.push(conn);
      }
    }
  });

  return serialConnections;
}

/**
 *
 * @param {D3Device} d3d
 */
export function getD3DevCenter(d3d) {
  var firstCenter = uDev.getCenterOfDevice_NoDOM(d3d.devices[0].deviceId);
  var lastCenter = uDev.getCenterOfDevice_NoDOM(d3d.devices[d3d.devices.length - 1].deviceId);
  return uMath.pointsCenter(firstCenter, lastCenter);
}

/**
 *
 * @param {number} pnid
 * @deprecated
 */
export function PNIDHasDefaultNote(pnid) {
  pnid = +pnid;
  var devicePNIDsWithDefaultNote = [287, 288, 289, 290, 291, 292, 293, 294, 295, 306];
  return devicePNIDsWithDefaultNote.indexOf(pnid) !== -1;
}

export function isCamera(dev) {
  return (typeof MovsenGlobals.camera_props === "object") && dev.product_name_id && (dev.product_name_id in MovsenGlobals.camera_props);
}

export function isThereCameraPlaced() {
  if (typeof PlannerGlobals.devices === "undefined") { return false; }
  return Array.isArray(PlannerGlobals.devices) && PlannerGlobals.devices.find(dev => isCamera(dev));
}

/**
 *
 * @param {Device} dev
 */
export function devNeedsAddon(dev) {
  const needAddonDevs = {
    296: 322,
    297: 322,
    298: 322,
  };
  if (dev && dev.product_name_id && dev.product_name_id in needAddonDevs) {
    return needAddonDevs[dev.product_name_id];
  }
  return false;
}

export function getD3DevTopCenter(d3Dev) {
  const rowsNum = getDRowsNum(d3Dev);
  const colsNum = getDColsNum(d3Dev);

  const base = {
    x: d3Dev.x,
    y: d3Dev.y
  };

  const center = {
    x: base.x + (colsNum / 2 * PlannerGlobals.DEVICE_SIZE) + ((colsNum - 1) / 2 * SERIAL_SPACING),
    y: base.y + (rowsNum / 2 * PlannerGlobals.DEVICE_SIZE) + ((rowsNum - 1) / 2 * SERIAL_SPACING)
  };

  const mid = {
    x: center.x,
    y: base.y
  };

  const angle = d3Dev.angle || 0;

  const snappedPt = rotatePointAroundAnotherXY(mid, center, angle);

  return snappedPt;
}

// returns true/wallId if snapped, false otherwise
export function isPointSnapped(point, returnWallId = false) {
  try {

    const room = whichRoomIsPointInInclOutdoors(point);
    if (!room) { return false; }
    const snapTreshold = Math.max(2 , PlannerGlobals.DEVICE_SIZE / 4); // it would be too small for DEVICE_SIZE = 2

    const allWalls = wallsOfRoom(room);
    if (room.id == uDev.OUTDOORS_ROOM_ID) {
      uMath.segmentsOfPolygon(PlannerGlobals.outerBorder).forEach(seg => allWalls.push(seg));
    }

    for (let i = 0; i < allWalls.length; i++) {
      const closestPt = segmentClosestPointToPoint_S(point, allWalls[i]);
      if (uMath.pointDistance(closestPt, point) < snapTreshold) {
        return returnWallId ? allWalls[i].id : true;
      }
    }

    return false;
  }
  catch (err) {
    console.debug(err);
    return false;
  }
}

// returns true/wallId if snapped, false otherwise
export function isD3DevSnapped(d3Dev, returnWallId = false) {
  const topCenter = getD3DevTopCenter(d3Dev);
  return isPointSnapped(topCenter, returnWallId);
}

/**
 *
 * @param {Point} centerPos
 */
export function getSuitablePlaceNearby(centerPos, initAngle, maxSnapDist = PlannerGlobals.DEVICE_SIZE, maxDevDist = 3, initDevDist = 1) {
  try {

    const wallsCacheSave = UDevGlobals.WALLS_CACHE;
    const roomOfCenterPos = whichRoomIsPointInInclOutdoors(centerPos);

    const angles = [270, 0, 180, 90,   // perpendicular angles
      45, 45 + 180, 45 + 90, 45 + 270]; // diagonal angles
    const MAX_DEV_DIST = maxDevDist;
    // try same room first if point was originating from a room
    if (roomOfCenterPos) {
      // try snappeds first in same room
      const outerBorderAllowed = uDev.outerBorderExists() && roomOfCenterPos.id == uDev.OUTDOORS_ROOM_ID;
      rebuildWallsCache(roomOfCenterPos, outerBorderAllowed, false);

      for (let i = initDevDist; i <= MAX_DEV_DIST; i++) {
        const distMult = i;
        for (let j = 0; j < angles.length; j++) {
          const angleMod = angles[j];
          const tryCenterPt = uMath.pointAtAngleDistance(centerPos, initAngle + angleMod, distMult * (PlannerGlobals.DEVICE_SIZE + 1));
          const snappedPt = snapDeviceToWall(tryCenterPt, maxSnapDist, 1);
          if (snappedPt) {
            const snapCenterPt = { x: snappedPt.point.x + PlannerGlobals.DEVICE_SIZE / 2, y: snappedPt.point.y + PlannerGlobals.DEVICE_SIZE / 2 };
            const placeTaken = uDev.isPlaceTaken(snapCenterPt, PlannerGlobals.devices, PlannerGlobals.DEVICE_SIZE * 0.9);
            if (!placeTaken) {
              UDevGlobals.WALLS_CACHE = wallsCacheSave;
              return snappedPt;
            }
          }
        }
      }

      // try non-snappeds in same room
      for (let i = initDevDist; i <= MAX_DEV_DIST; i++) {
        const distMult = i;
        for (let j = 0; j < angles.length; j++) {
          const angleMod = angles[j];
          const tryCenterPt = uMath.pointAtAngleDistance(centerPos, initAngle + angleMod, distMult * (PlannerGlobals.DEVICE_SIZE + 1));
          const roomOfPt = whichRoomIsPointInInclOutdoors(tryCenterPt);
          if (roomOfPt && roomOfPt.id == roomOfCenterPos.id) {
            const topLeftPt = { x: tryCenterPt.x - PlannerGlobals.DEVICE_SIZE / 2, y: tryCenterPt.y - PlannerGlobals.DEVICE_SIZE / 2 };
            const doesFit = doesDeviceFitRoom(roomOfPt.id, topLeftPt, initAngle /* + angleMod */, 1);
            if (doesFit) {
              const placeTaken = uDev.isPlaceTaken(tryCenterPt, PlannerGlobals.devices, PlannerGlobals.DEVICE_SIZE * 0.9);
              if (!placeTaken) {
                UDevGlobals.WALLS_CACHE = wallsCacheSave;
                return { point: topLeftPt, rotation: initAngle /* + angleMod */, room: roomOfPt, wall: null };
              }
            }
          }

        }
      }
    }

    // if couldnt snap, regen normal walls cache
    rebuildWallsCache(false, true, true);

    // try snap to any room
    for (let i = initDevDist; i <= MAX_DEV_DIST; i++) {
      const distMult = i;
      for (let j = 0; j < angles.length; j++) {
        const angleMod = angles[j];
        const tryCenterPt = uMath.pointAtAngleDistance(centerPos, initAngle + angleMod, distMult * (PlannerGlobals.DEVICE_SIZE + 1));
        const snappedPt = snapDeviceToWall(tryCenterPt, maxSnapDist, 1);
        if (snappedPt) {
          const snapCenterPt = { x: snappedPt.point.x + PlannerGlobals.DEVICE_SIZE / 2, y: snappedPt.point.y + PlannerGlobals.DEVICE_SIZE / 2 };
          const placeTaken = uDev.isPlaceTaken(snapCenterPt, PlannerGlobals.devices, PlannerGlobals.DEVICE_SIZE * 0.9);
          if (!placeTaken) {
            UDevGlobals.WALLS_CACHE = wallsCacheSave;
            return snappedPt;
          }
        }
      }
    }

    // try non-snappeds in any room
    for (let i = 1; i <= MAX_DEV_DIST; i++) {
      const distMult = i;
      for (let j = 0; j < angles.length; j++) {
        const angleMod = angles[j];
        const tryCenterPt = uMath.pointAtAngleDistance(centerPos, initAngle + angleMod, distMult * (PlannerGlobals.DEVICE_SIZE + 1));
        const roomOfPt = whichRoomIsPointInInclOutdoors(tryCenterPt);
        if (roomOfPt) {
          const topLeftPt = { x: tryCenterPt.x - PlannerGlobals.DEVICE_SIZE / 2, y: tryCenterPt.y - PlannerGlobals.DEVICE_SIZE / 2 };
          const doesFit = doesDeviceFitRoom(roomOfPt.id, topLeftPt, initAngle /* + angleMod */, 1);
          if (doesFit) {
            const placeTaken = uDev.isPlaceTaken(tryCenterPt, PlannerGlobals.devices, PlannerGlobals.DEVICE_SIZE * 0.9);
            if (!placeTaken) {
              UDevGlobals.WALLS_CACHE = wallsCacheSave;
              return { point: topLeftPt, rotation: initAngle /* + angleMod */, room: roomOfPt, wall: null };
            }
          }
        }

      }
    }

    UDevGlobals.WALLS_CACHE = wallsCacheSave;
    return false;
  }
  catch (err) {
    console.debug(err);
    return false;
  }
}

/**
 *
 * @param {Serial} serial
 */
export function getSerialCenterPos2(serial) {
  if (!serial) { return { x: -1, y: -1 }; }
  const firstDevCenter = uDev.getCenterOfDevice_NoDOM(serial.deviceIds[0]);
  var serLen = serialRealLength(serial);
  var serCenterDiff = (PlannerGlobals.DEVICE_SIZE + SERIAL_SPACING) * (serLen - 1) / 2;
  const baseAngle = getSerialAngle2(serial);
  const rotAngl = serial.direction === "horizontal" ? baseAngle : baseAngle + 90;
  return uMath.pointAtAngleDistance(firstDevCenter, rotAngl, serCenterDiff);
}

/**
 *
 * @param {Serial} serial
 * @returns
 */
export function getSerialAngle2(serial) {
  if (!serial) { return 0; }
  if (thereWasGhostDevice()) {
    var props = ghostDevProps();
    if (props.entityType == "serial" && props.entityId == serial.id) {
      return props.rot;
    }
  }
  const firstDev = getDeviceById(serial.deviceIds[0]);
  if (!firstDev) {
    return 0;
  }
  return firstDev.angle || 0;
}

/**
 *
 * @param {Point} point
 */
export function isPointOutOfBounds(point) {
  if (!point) { return false; }
  return !uMath.rectContainsPoint({ x: 0, y: 0, width: 900, height: 900 }, point);
}

/**
 *
 * @param {Device} dev
 * @returns {Device}
 */
export function getFirstOccurrenceOfDev(dev) {
  if (!isDeviceMerged(dev)) {
    return dev;
  }
  var serialOfDev = getSerialOfDeviceId(dev.deviceId);
  if (serialOfDev) {
    return serialOfDev.deviceIds.map(getDeviceById).find(function (elem) {
      return elem.product_name_id == dev.product_name_id
    });
  } else if (dev.serializable && !serialOfDev) {
    return dev;
  } else {
    return PlannerGlobals.devices.find(function (currDev) {
      return currDev.roomId == dev.roomId && currDev.product_name_id
          == dev.product_name_id;
    });
  }
}